import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "../../app/store"
import { IPages } from "../../shared/types/IPages"

export interface ApplicationState {
  activePage: IPages
  isQueueEnabled: boolean
  isSearchEnabled: boolean
  isListenOnEnabled: boolean
  isDateEnabled: boolean
  listenOnText: string
  articlesPageHeading: string
  clickHereCtaText: string
  clickHereCtaLink: string
}

const initialState: ApplicationState = {
  activePage: IPages.ARTICLES,
  isQueueEnabled: true,
  isSearchEnabled: true,
  isDateEnabled: true,
  isListenOnEnabled: true,
  listenOnText: "Listen on Noa",
  articlesPageHeading: "Coming up",
  clickHereCtaText: "Click here for more audio articles.",
  clickHereCtaLink: "https://www.newsoveraudio.com/latest",
}

export const applicationSlice = createSlice({
  name: "application",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setActivePage: (state, action: PayloadAction<IPages>) => {
      state.activePage = action.payload
    },
    setApplicationOptions: state => {
      const action = { payload: new URLSearchParams(window.location.search) }

      const isCorrectValue = (value: string | null) =>
        value && ["1", "0"].includes(value)

      const isQueueEnabled = action.payload.get("queue")
      const isSearchEnabled = action.payload.get("search")
      const isListenOnEnabled = action.payload.get("listenOn")
      const isDateEnabled = action.payload.get("date")

      state.isDateEnabled = isCorrectValue(isDateEnabled)
        ? !!Number(isDateEnabled)
        : state.isDateEnabled
      state.isQueueEnabled = isCorrectValue(isQueueEnabled)
        ? !!Number(isQueueEnabled)
        : state.isQueueEnabled
      state.isSearchEnabled = isCorrectValue(isSearchEnabled)
        ? !!Number(isSearchEnabled)
        : state.isSearchEnabled
      state.isListenOnEnabled = isCorrectValue(isListenOnEnabled)
        ? !!Number(isListenOnEnabled)
        : state.isListenOnEnabled

      state.listenOnText =
        action.payload.get("listenOnText") || state.listenOnText
      state.articlesPageHeading =
        action.payload.get("articlesPageHeading") || state.articlesPageHeading
      state.clickHereCtaText =
        action.payload.get("clickHereCtaText") || state.clickHereCtaText
      state.clickHereCtaLink =
        action.payload.get("clickHereCtaLink") || state.clickHereCtaLink
    },
  },
})

export const { setActivePage, setApplicationOptions } = applicationSlice.actions
export const selectActivePage = (state: RootState) =>
  state.application.activePage
export const selectIsQueueEnabled = (state: RootState) =>
  state.application.isQueueEnabled
export const selectIsSearchEnabled = (state: RootState) =>
  state.application.isSearchEnabled
export const selectIsDateEnabled = (state: RootState) =>
  state.application.isDateEnabled
export const selectIsListenOnEnabled = (state: RootState) =>
  state.application.isListenOnEnabled
export const selectListenOnText = (state: RootState) =>
  state.application.listenOnText
export const selectArticlesPageHeading = (state: RootState) =>
  state.application.articlesPageHeading
export const selectClickHereCtaText = (state: RootState) =>
  state.application.clickHereCtaText
export const selectClickHereCtaLink = (state: RootState) =>
  state.application.clickHereCtaLink

export default applicationSlice.reducer
